/**
 * @fileoverview Error boundary component to catch lazy loading errors.
 * @package PlayItNow
 * @module apps/front/components/ErrorBoundary
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { Component, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle, faHome } from '@fortawesome/free-solid-svg-icons'

/**
 * @summary Props for the ErrorBoundary component.
 */
interface ErrorBoundaryProps {
  /** Child components to wrap */
  children: ReactNode
  /** Optional custom fallback UI */
  fallback?: ReactNode
}

/**
 * @summary Internal state for the ErrorBoundary component.
 */
interface ErrorBoundaryState {
  /** Whether an error was caught */
  hasError: boolean
  /** The caught error object */
  error: Error | null
}

/**
 * @summary Error boundary to catch lazy loading and rendering errors.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return <ErrorFallback error={this.state.error} />
    }

    return this.props.children
  }
}

/**
 * @summary Fallback UI for errors.
 */
function ErrorFallback({ error }: { error: Error | null }) {
  // We can't use hooks in a class component, so we'll use a functional component here
  // But we need to handle translation differently
  const errorMessage = error?.message || 'Une erreur est survenue'

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-2xl w-full text-center">
        <div className="mb-8">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-6xl text-red-500 dark:text-red-400 mb-4"
          />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Erreur de chargement
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-2">{errorMessage}</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
            Veuillez réessayer ou retourner à la page d'accueil.
          </p>
        </div>

        <div className="flex flex-col gap-3 max-w-md mx-auto">
          <button
            onClick={() => window.location.reload()}
            className="btn btn-primary flex items-center justify-center gap-2"
          >
            <span>Recharger la page</span>
          </button>
          <Link to="/" className="btn btn-outline flex items-center justify-center gap-2">
            <FontAwesomeIcon icon={faHome} className="w-4 h-4" />
            <span>Retour à l'accueil</span>
          </Link>
        </div>
      </div>
    </div>
  )
}
